import React, { useState, useEffect, useCallback, useRef } from 'react';
import styles from './index.module.css';
import SettingsModal from './components/SettingsModal';
import ControllerModal from './components/ControllerModal';

function MatchBoard() {

    useEffect(() => {
      document.body.style.backgroundColor = 'var(--gray-950)';
  
      return () => {
        document.body.style.backgroundColor = '';
      };
    }, []);
  // 매치 상태 관리
  const [matchState, setMatchState] = useState({
    scores: {
      blue: 0,
      red: 0,
      yellow: 0
    },
    currentQuarter: 1,
    remainingTime: 0,
    isPlaying: false,
    currentKeeper: 1,
    nextKeeper: 2
  });

  // 설정 상태 관리
  const [settings, setSettings] = useState({
    totalTime: 120, // 2시간 (분 단위)
    matchType: '6vs6', // 기본 매치 타입
    totalPlayers: 18, // 기본 참가자 수
    totalTeams: 3,
    quarters: 9,
    quarterTime: 12,
    keepersPerQuarter: 1
  });

  // 쿼터별 스코어 히스토리 추가
  const [quarterHistory, setQuarterHistory] = useState([]);

  // 쿼터별 팀 구성을 저장할 상태
  const [quarterTeams, setQuarterTeams] = useState({});

  // 매치 설정이 변경될 때 쿼터별 팀 구성 초기화
  useEffect(() => {
    const teams = ['blue', 'red', 'yellow'].slice(0, settings.totalTeams);
    const newQuarterTeams = {};
    
    // 각 쿼터별로 2개 팀씩 순차적으로 매칭
    for (let i = 1; i <= settings.quarters; i++) {
      const firstTeamIndex = (i - 1) % teams.length;
      const secondTeamIndex = (firstTeamIndex + 1) % teams.length;
      newQuarterTeams[i] = [teams[firstTeamIndex], teams[secondTeamIndex]];
    }

    setQuarterTeams(newQuarterTeams);
  }, [settings.quarters, settings.totalTeams]);

  // 현재 쿼터의 팀 가져오기
  const getCurrentQuarterTeams = (quarter) => {
    return quarterTeams[quarter] || [];
  };

  // 컴포넌트 마운트 시 초기화
  useEffect(() => {
    // 설정된 쿼터 수에 맞게 팀 구성 업데이트
    setQuarterTeams(prev => {
      const newTeams = {};
      for (let i = 1; i <= settings.quarters; i++) {
        newTeams[i] = prev[i] || getCurrentMatchTeams();
      }
      return newTeams;
    });
  }, [settings.quarters]);

  // 전역 타이머 설정
  useEffect(() => {
    const timer = setInterval(() => {
      // 모든 진행 중인 쿼터의 시간 업데이트
      setQuarterHistory(prev => prev.map(quarter => {
        if (quarter.isPlaying && quarter.remainingTime > 0) {
          const newRemainingTime = quarter.remainingTime - 4000;  // 1000ms씩 감소 (10배 빠르게)
          return {
            ...quarter,
            remainingTime: newRemainingTime,
            isPlaying: newRemainingTime > 0
          };
        }
        return quarter;
      }));

      // 현재 표시 중인 쿼터의 상태를 quarterHistory와 동기화
      setMatchState(prev => {
        const currentQuarterData = quarterHistory.find(q => q.quarter === prev.currentQuarter);
        if (currentQuarterData) {
          return {
            ...prev,
            remainingTime: currentQuarterData.remainingTime,
            isPlaying: currentQuarterData.isPlaying
          };
        }
        return prev;
      });
    }, 100);  // 100ms 간격으로 업데이트

    return () => clearInterval(timer);
  }, [quarterHistory]);

  // matchState와 quarterHistory 동기화를 위한 추가 useEffect
  useEffect(() => {
    const currentQuarterData = quarterHistory.find(q => q.quarter === matchState.currentQuarter);
    if (currentQuarterData) {
      setMatchState(prev => ({
        ...prev,
        remainingTime: currentQuarterData.remainingTime,
        isPlaying: currentQuarterData.isPlaying
      }));
    }
  }, [quarterHistory, matchState.currentQuarter]);

  // 시간 포맷팅 함수 수정
  const formatTime = (milliseconds) => {
    if (milliseconds <= 0) return "00:00";
    
    const totalSeconds = Math.floor(milliseconds / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  // 컨트롤러 함수들
  const startQuarter = () => {
    const currentQuarter = matchState.currentQuarter;
    const currentTeams = getCurrentMatchTeams();
    
    // 새로운 쿼터 데이터 생성
    const newQuarterData = {
      quarter: currentQuarter,
      scores: { ...matchState.scores }, // 현재 상태의 점수 사용
      teams: currentTeams,
      remainingTime: settings.quarterTime * 60 * 1000,
      isPlaying: true
    };

    // quarterHistory에 추가
    setQuarterHistory(prev => {
      const existingQuarterIndex = prev.findIndex(q => q.quarter === currentQuarter);
      
      if (existingQuarterIndex >= 0) {
        // 기존 쿼터 업데이트
        const updatedHistory = [...prev];
        updatedHistory[existingQuarterIndex] = {
          ...updatedHistory[existingQuarterIndex],
          isPlaying: true,
          remainingTime: settings.quarterTime * 60 * 1000
        };
        return updatedHistory;
      } else {
        // 새 쿼터 추가
        return [...prev, newQuarterData];
      }
    });

    // matchState 업데이트
    setMatchState(prev => ({
      ...prev,
      isPlaying: true
    }));
  };

  const pauseTime = () => {
    const currentQuarter = matchState.currentQuarter;
    const quarterData = quarterHistory.find(q => q.quarter === currentQuarter);
    const newIsPlaying = !matchState.isPlaying;

    if (quarterData) {
      // quarterHistory 업데이트
      setQuarterHistory(prev => prev.map(q => 
        q.quarter === currentQuarter
          ? { ...q, isPlaying: newIsPlaying }
          : q
      ));
    }

    // matchState 업데이트
    setMatchState(prev => ({
      ...prev,
      isPlaying: newIsPlaying
    }));
  };

  const resetTime = () => {
    // matchState 업데이트
    setMatchState(prev => ({
      ...prev,
      remainingTime: settings.quarterTime * 60 * 1000,
      isPlaying: false
    }));

    // quarterHistory 업데이트
    setQuarterHistory(prev => prev.map(quarter => {
      if (quarter.quarter === matchState.currentQuarter) {
        return {
          ...quarter,
          remainingTime: settings.quarterTime * 60 * 1000,
          isPlaying: false
        };
      }
      return quarter;
    }));
  };

  // 초기화 버튼 클릭 핸들러
  const onResetTime = () => {
    if (window.confirm('시간을 초기화하시겠습니까?')) {
      resetTime();
    }
  };

  // 현재 쿼터의 스코어를 저장하는 함수
  const saveCurrentQuarterScore = (currentQuarter) => {
    const existingQuarterData = quarterHistory.find(h => h.quarter === currentQuarter);
    if (existingQuarterData) {
      // 기존 쿼터 데이터 업데이트
      setQuarterHistory(prev => prev.map(h => 
        h.quarter === currentQuarter 
          ? { 
              ...h, 
              scores: { ...matchState.scores },
              remainingTime: matchState.remainingTime,
              isPlaying: matchState.isPlaying
            }
          : h
      ));
    } else {
      // 새로운 쿼터 데이터 추가
      setQuarterHistory(prev => [...prev, {
        quarter: currentQuarter,
        scores: { ...matchState.scores },
        teams: quarterTeams[currentQuarter] || [],
        remainingTime: matchState.remainingTime,
        isPlaying: matchState.isPlaying
      }]);
    }
  };

  // 쿼터 선택 핸들러 수정
  const handleQuarterSelect = (quarter) => {
    setSelectedQuarter(quarter);
    
    const quarterData = quarterHistory.find(q => q.quarter === quarter);
    const quarterTeamList = getCurrentQuarterTeams(quarter);

    if (quarterData) {
      // 이미 존재하는 쿼터의 경우 저장된 상태 사용
      setMatchState({
        currentQuarter: quarter,
        scores: quarterData.scores,
        remainingTime: quarterData.remainingTime,
        isPlaying: quarterData.isPlaying
      });
    } else {
      // 새로운 쿼터의 경우 점수를 0으로 초기화
      const initialScores = quarterTeamList.reduce((acc, team) => {
        acc[team] = 0;
        return acc;
      }, {});

      // 새 쿼터 데이터를 quarterHistory에 추가
      setQuarterHistory(prev => [...prev, {
        quarter: quarter,
        scores: initialScores,
        teams: quarterTeamList,
        remainingTime: settings.quarterTime * 60 * 1000,
        isPlaying: false
      }]);

      setMatchState({
        currentQuarter: quarter,
        scores: initialScores,
        remainingTime: settings.quarterTime * 60 * 1000,
        isPlaying: false
      });
    }
  };

  // 쿼터 종료 함수 수정
  const endQuarter = () => {
    if (window.confirm('현재 쿼터를 종료하시겠습니까?')) {
      // 현재 쿼터의 스코어와 시간 저장
      saveCurrentQuarterScore(matchState.currentQuarter);

      // 다음 쿼터로 이동
      const nextQuarter = matchState.currentQuarter + 1;
      if (nextQuarter <= settings.quarters) {
        const wasPlaying = matchState.isPlaying; // 현재 진행 상태 저장
        
        // 다음 쿼터 데이터 확인
        const nextQuarterData = quarterHistory.find(h => h.quarter === nextQuarter);
        
        if (nextQuarterData && nextQuarterData.remainingTime > 0) {
          // 이미 시작된 쿼터면 이전 상태 유지
          handleQuarterSelect(nextQuarter);
        } else {
          // 새로운 쿼터면 정지 상태로 시작
          setMatchState(prev => ({
            ...prev,
            currentQuarter: nextQuarter,
            scores: getCurrentQuarterTeams(nextQuarter).reduce((acc, team) => {
              acc[team] = 0;
              return acc;
            }, {}),
            remainingTime: settings.quarterTime * 60 * 1000,
            isPlaying: false
          }));
          setSelectedQuarter(nextQuarter);
        }
      }
    }
  };

  // 팀재 쿼터에서 경기 중인 팀 계산
  const getCurrentMatchTeams = () => {
    return getCurrentQuarterTeams(matchState.currentQuarter);
  };

  // 점수 업데이트 함수 수정
  const updateScore = (team, value) => {
    // 현재 쿼터의 점수 업데이트
    setMatchState(prev => {
      const newScores = {
        ...prev.scores,
        [team]: Math.max(0, prev.scores[team] + value)
      };

      // quarterHistory 업데이트
      setQuarterHistory(prevHistory => {
        const existingQuarterIndex = prevHistory.findIndex(q => q.quarter === prev.currentQuarter);
        
        if (existingQuarterIndex >= 0) {
          // 기존 쿼터 데이터 업데이트
          const updatedHistory = [...prevHistory];
          updatedHistory[existingQuarterIndex] = {
            ...updatedHistory[existingQuarterIndex],
            scores: newScores
          };
          return updatedHistory;
        } else {
          // 새 쿼터 데이터 추가
          return [...prevHistory, {
            quarter: prev.currentQuarter,
            scores: newScores,
            teams: getCurrentQuarterTeams(prev.currentQuarter),
            remainingTime: settings.quarterTime * 60 * 1000,
            isPlaying: false
          }];
        }
      });

      return {
        ...prev,
        scores: newScores
      };
    });
  };

  // 팀별 인원 수 계산
  const calculateTeamSizes = () => {
    const totalTeams = settings.matchType === '6vs6' 
      ? (settings.totalPlayers >= 17 ? 3 : 2)
      : (settings.totalPlayers >= 13 ? 3 : 2);
    
    const baseSize = Math.floor(settings.totalPlayers / totalTeams);
    const remainder = settings.totalPlayers % totalTeams;
    
    return Array(totalTeams).fill(baseSize).map((size, index) => 
      index < remainder ? size + 1 : size
    );
  };

  // 키퍼 순서 계산
  const calculateKeepers = () => {
    const teamSizes = calculateTeamSizes();
    const maxTeamSize = Math.max(...teamSizes);
    const keepersPerQuarter = (settings.matchType === '6vs6' && settings.totalPlayers >= 17) || 
                             (settings.matchType === '5vs5' && settings.totalPlayers >= 13) ? 2 : 1;
    
    const currentQuarterIndex = matchState.currentQuarter - 1;
    const keepersStartIndex = maxTeamSize - (currentQuarterIndex * keepersPerQuarter);
    
    return {
      current: Array(keepersPerQuarter).fill(0)
        .map((_, i) => keepersStartIndex - i)
        .filter(num => num > 0),
      next: Array(keepersPerQuarter).fill(0)
        .map((_, i) => keepersStartIndex - keepersPerQuarter - i)
        .filter(num => num > 0)
    };
  };

  // 팀별 키퍼 순서 계산
  const calculateTeamKeepers = (team) => {
    const teamSizes = calculateTeamSizes();
    const teamIndex = ['blue', 'red', 'yellow'].indexOf(team);
    const teamSize = teamSizes[teamIndex];
    
    // 번호 순서대로 배열 생성
    const numbers = Array(teamSize)
      .fill(0)
      .map((_, i) => teamSize - i);
    
    return numbers.join(' → ');
  };

  // 현별 첫 쿼터 계산
  const getTeamFirstQuarter = (team) => {
    const matches = [
      ['blue', 'red'],    // 1쿼터
      ['blue', 'yellow'], // 2쿼터
      ['red', 'yellow']   // 3쿼터
    ];

    return matches.findIndex(match => match.includes(team)) + 1;
  };

  // 팀별 쿼터 수 계산
  const getTeamQuarterCount = (team) => {
    const currentQuarter = matchState.currentQuarter;
    const firstQuarter = getTeamFirstQuarter(team);
    let quarterCount = 0;
    
    // 현재 쿼터까지 해당 팀이 몇 번 경기했는지 계산
    for (let q = firstQuarter; q <= currentQuarter; q++) {
      const teamsInQuarter = getCurrentQuarterTeams(q);
      if (teamsInQuarter.includes(team)) {
        quarterCount++;
      }
    }
    
    return quarterCount - 1; // 첫 쿼터를 0으로 시작하기 위해 1을 뺌
  };

  // 현재 쿼터의 팀별 키퍼 계산
  const calculateCurrentKeeper = (team) => {
    const currentTeams = getCurrentMatchTeams();
    if (!currentTeams.includes(team)) {
      return null;
    }

    const teamSizes = calculateTeamSizes();
    const teamIndex = ['blue', 'red', 'yellow'].indexOf(team);
    const teamSize = teamSizes[teamIndex];
    const keepersPerQuarter = settings.keepersPerQuarter;
    
    const quarterCount = getTeamQuarterCount(team);
    const startPosition = (quarterCount * keepersPerQuarter) % teamSize;
    
    const currentKeepers = Array(keepersPerQuarter)
      .fill(0)
      .map((_, index) => {
        let position = (startPosition + index) % teamSize;
        let number = teamSize - position;
        return number;
      })
      .filter(num => num > 0);

    return currentKeepers.length > 0 ? { label: '골키퍼', numbers: currentKeepers } : null;
  };

  // 다음 쿼터의 키퍼 계산
  const calculateNextKeeper = (team) => {
    const currentTeams = getCurrentMatchTeams();
    if (!currentTeams.includes(team)) {
      return null;
    }

    const teamSizes = calculateTeamSizes();
    const teamIndex = ['blue', 'red', 'yellow'].indexOf(team);
    const teamSize = teamSizes[teamIndex];
    const keepersPerQuarter = settings.keepersPerQuarter;
    
    // 팀별 첫 쿼터부터 카운트하여 다음 키퍼 계산
    const quarterCount = getTeamQuarterCount(team);
    const startPosition = ((quarterCount + 1) * keepersPerQuarter) % teamSize;
    
    const nextKeepers = Array(keepersPerQuarter)
      .fill(0)
      .map((_, index) => {
        let position = (startPosition + index) % teamSize;
        let number = teamSize - position;
        return number;
      })
      .filter(num => num > 0);

    return nextKeepers.length > 0 ? `Next GK ${nextKeepers.join(', ')}` : '';
  };

  const [forceKeeperChange, setForceKeeperChange] = useState(false);

  const shouldShowKeeperChange = () => {
    // 강제 키퍼 교체가 활성화되었거나
    if (forceKeeperChange) return true;

    // 기존 조건들
    const keepersPerQuarter = calculateKeepers().current.length;
    if (keepersPerQuarter < 2) return false;
    if (!matchState.isPlaying) return false;
    
    const quarterTimeInMs = settings.quarterTime * 60 * 1000;
    const halfQuarterTime = quarterTimeInMs / 2;
    return matchState.remainingTime <= halfQuarterTime;
  };

  const handleForceKeeperChange = () => {
    playBuzzerSound();
    setForceKeeperChange(true);
    setTimeout(() => {
      setForceKeeperChange(false);
    }, 30000);
  };

  const [showSettingsModal, setShowSettingsModal] = useState(false);

  // 화면 크기 감지
  const [isWideScreen, setIsWideScreen] = useState(window.innerWidth >= 768);

  useEffect(() => {
    const handleResize = () => {
      setIsWideScreen(window.innerWidth >= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // 재생 버튼 텍스트 결정 함수 수정
  const getPlayButtonText = () => {
    // 현재 쿼터가 아직 시작되지 않았는지 확인
    const quarterData = quarterHistory.find(h => h.quarter === matchState.currentQuarter);
    const isQuarterStarted = quarterData && quarterData.remainingTime !== settings.quarterTime * 60 * 1000;

    if (!isQuarterStarted && matchState.remainingTime === settings.quarterTime * 60 * 1000) {
      return '시작';
    }
    
    // 시간이 모두 소진되었는지 확인
    if (matchState.remainingTime <= 0) {
      return '종료';
    }
    
    return matchState.isPlaying ? '일시 정지' : '재개';
  };

  // 재생 버튼 스타일 결정 함수 수정
  const getPlayButtonStyle = () => {
    const quarterData = quarterHistory.find(h => h.quarter === matchState.currentQuarter);
    const isQuarterStarted = quarterData && quarterData.remainingTime !== settings.quarterTime * 60 * 1000;

    if (!isQuarterStarted && matchState.remainingTime === settings.quarterTime * 60 * 1000) {
      return styles.start;
    }
    
    // 시간이 모두 소진되었을 때의 스타일
    if (matchState.remainingTime <= 0) {
      return styles.ended;
    }
    
    return matchState.isPlaying ? styles.pause : styles.resume;
  };

  // 재생 버튼 클릭 핸들러
  const handlePlayButton = () => {
    const quarterData = quarterHistory.find(h => h.quarter === matchState.currentQuarter);
    const isQuarterStarted = quarterData && quarterData.remainingTime !== settings.quarterTime * 60 * 1000;

    if (!isQuarterStarted && matchState.remainingTime === settings.quarterTime * 60 * 1000) {
      startQuarter();
    } else {
      pauseTime();
    }
  };

  const onEndQuarter = () => {
    if (window.confirm('현재 쿼터를 종료하시겠습니까?')) {
      endQuarter();
    }
  };

  const [selectedQuarter, setSelectedQuarter] = useState(1);

  // 쿼터 리스트 생성
  const generateQuarterList = () => {
    const list = [];
    for (let i = 1; i <= settings.quarters; i++) {
      const quarterData = quarterHistory.find(h => h.quarter === i);
      const quarterTeamList = quarterTeams[i] || [];

      if (quarterData) {
        // 이미 종료된 쿼터는 저장된 데이터 사용
        list.push({
          quarter: i,
          scores: quarterData.scores,
          teams: quarterTeamList,
          remainingTime: quarterData.remainingTime,
          isPlaying: quarterData.isPlaying
        });
      } else {
        // 진행 중이거나 시작하지 않은 쿼터
        list.push({
          quarter: i,
          scores: i === matchState.currentQuarter ? matchState.scores : 
            quarterTeamList.reduce((acc, team) => {
              acc[team] = 0;
              return acc;
            }, {}),
          teams: quarterTeamList,
          remainingTime: settings.quarterTime * 60 * 1000,
          isPlaying: false
        });
      }
    }
    return list;
  };

  // 현재 표시할 스코어 계산
  const getDisplayScores = () => {
    const quarterData = quarterHistory.find(h => h.quarter === selectedQuarter);
    if (quarterData) {
      return quarterData.scores;
    }
    
    // quarterHistory에 데이터가 없는 경우 현재 matchState의 scores 사용
    return selectedQuarter === matchState.currentQuarter 
      ? matchState.scores 
      : getCurrentQuarterTeams(selectedQuarter).reduce((acc, team) => {
          acc[team] = 0;
          return acc;
        }, {});
  };

  const handleSettingsChange = (newSettings) => {
    setSettings(newSettings);
    
    // 보드 초기화
    setMatchState({
      currentQuarter: 1,
      remainingTime: newSettings.quarterTime * 60 * 1000, // 분을 밀리초로 변환
      isPlaying: false,
      scores: newSettings.totalTeams === 3 
        ? { blue: 0, red: 0, yellow: 0 }
        : { blue: 0, red: 0 }
    });
    
    // 쿼터 히스토리 초기화
    setQuarterHistory([]);
    
    // 선택된 쿼터 초기화
    setSelectedQuarter(1);
  };

  // 초터 수와 시간 계산
  const calculateQuarterSettings = () => {
    if ((settings.matchType === '6vs6' && settings.totalPlayers >= 17) || 
        (settings.matchType === '5vs5' && settings.totalPlayers >= 13)) {
      return { quarters: 9, quarterTime: 12 };
    } else if (settings.matchType === '6vs6') {
      if (settings.totalPlayers >= 15) {
        return { quarters: 8, quarterTime: 15 };
      } else {
        return { quarters: 6, quarterTime: 15 };
      }
    } else { // 5vs5
      if (settings.totalPlayers >= 11) {
        return { quarters: 8, quarterTime: 15 };
      } else {
        return { quarters: 6, quarterTime: 15 };
      }
    }
  };

  // 쿼터당 키퍼 수 계산
  const calculateKeepersPerQuarter = () => {
    if ((settings.matchType === '6vs6' && settings.totalPlayers >= 17) || 
        (settings.matchType === '5vs5' && settings.totalPlayers >= 13)) {
      return 2;
    }
    return 1;
  };

  // 초기 설정값으로 보드 세팅
  useEffect(() => {
    const quarterSettings = calculateQuarterSettings();
    const totalTeams = settings.matchType === '6vs6' 
      ? (settings.totalPlayers >= 17 ? 3 : 2)
      : (settings.totalPlayers >= 13 ? 3 : 2);

    const initialSettings = {
      ...settings,
      totalTeams,
      quarters: quarterSettings.quarters,
      quarterTime: quarterSettings.quarterTime,
      keepersPerQuarter: calculateKeepersPerQuarter()
    };

    setSettings(initialSettings);
    
    // 보드 초기화
    setMatchState({
      currentQuarter: 1,
      remainingTime: initialSettings.quarterTime * 60 * 1000,
      isPlaying: false,
      scores: initialSettings.totalTeams === 3 
        ? { blue: 0, red: 0, yellow: 0 }
        : { blue: 0, red: 0 }
    });
    
    // 쿼터 히스토리 초기화
    setQuarterHistory([]);
    
    // 선택된 쿼터 초기화
    setSelectedQuarter(1);
  }, []); // 컴포넌트 마운트 시 한 번만 실행

  // 현재 표시할 시간 계산
  const getDisplayTime = () => {
    if (matchState.remainingTime === 0) {
      return formatTime(settings.quarterTime * 60 * 1000);
    }
    return formatTime(matchState.remainingTime);
  };

  // Audio 객체를 state로 관리
  const [buzzerSound, setBuzzerSound] = useState(null);
  const [soundCount, setSoundCount] = useState(0);

  // 사운드 종료 핸들러를 useRef로 변경하여 무한 루프 방지
  const soundCountRef = useRef(0);
  
  // 컴포넌트 마운트 시 Audio 객체 초기화
  useEffect(() => {
    const audio = new Audio('/sounds/buzzer.mp3');
    audio.volume = 0.5;  // 초기 볼륨 설정
    
    // 사운드 종료 이벤트 핸들러
    const handleSoundEnd = () => {
      if (soundCountRef.current < 4) { // 9번까지만 재시작 (총 10번 재생)
        soundCountRef.current += 1;
        audio.currentTime = 0;
        audio.play().catch(error => {
          console.log('부저 소리 재생 실패:', error);
        });
      } else {
        soundCountRef.current = 0;
      }
    };
    
    // 사운드 종료 이벤트 리스너 추가
    audio.addEventListener('ended', handleSoundEnd);
    setBuzzerSound(audio);

    // 클린업 함수
    return () => {
      if (audio) {
        audio.removeEventListener('ended', handleSoundEnd);
      }
    };
  }, []); // 의존성 배열을 비워서 마운트 시에만 실행

  // 사운드 재생 함수
  const playBuzzerSound = () => {
    if (buzzerSound) {
      soundCountRef.current = 0; // 카운트 리셋
      buzzerSound.currentTime = 0;
      buzzerSound.play().catch(error => {
        console.log('부저 소리 재생 실패:', error);
      });
    }
  };

  // 키퍼 교체 알림을 위한 state 추가
  const [showKeeperChangeAlert, setShowKeeperChangeAlert] = useState(false);

  // 키퍼 교체 알림 함수
  const checkKeeperChange = (currentTime, prevTime) => {
    const keeperChangeTime = 60 * 1000;
    const currentMinute = Math.floor(currentTime / (60 * 1000));
    const prevMinute = Math.floor(prevTime / (60 * 1000));

    if (currentMinute !== prevMinute && currentMinute % 2 === 0) {
      playBuzzerSound();
      setShowKeeperChangeAlert(true);
      setTimeout(() => {
        setShowKeeperChangeAlert(false);
      }, 3000);
    }
  };

  // 타이머 useEffect 수정
  useEffect(() => {
    const timer = setInterval(() => {
      setQuarterHistory(prev => prev.map(quarter => {
        if (quarter.isPlaying && quarter.remainingTime > 0) {
          const newRemainingTime = quarter.remainingTime - 100;
          
          // 키퍼 교체 체크
          if (quarter.quarter === matchState.currentQuarter) {
            checkKeeperChange(newRemainingTime, quarter.remainingTime);
          }

          return {
            ...quarter,
            remainingTime: newRemainingTime,
            isPlaying: newRemainingTime > 0
          };
        }
        return quarter;
      }));

      // ... 나머지 코드 ...
    }, 100);

    return () => clearInterval(timer);
  }, [quarterHistory]);

  // 점수 변경 함수 수정
  const handleScoreChange = (team, change) => {
    // 현재 쿼터의 점수 업데이트
    setMatchState(prev => {
      const newScores = {
        ...prev.scores,
        [team]: Math.max(0, prev.scores[team] + change)
      };

      // quarterHistory 즉시 업데이트
      setQuarterHistory(prevHistory => {
        const existingQuarterIndex = prevHistory.findIndex(q => q.quarter === prev.currentQuarter);
        
        if (existingQuarterIndex >= 0) {
          // 기존 쿼터 데이터 업데이트
          const updatedHistory = [...prevHistory];
          updatedHistory[existingQuarterIndex] = {
            ...updatedHistory[existingQuarterIndex],
            scores: newScores
          };
          return updatedHistory;
        } else {
          // 새 쿼터 데이터 추가
          return [...prevHistory, {
            quarter: prev.currentQuarter,
            scores: newScores,
            teams: getCurrentQuarterTeams(prev.currentQuarter),
            remainingTime: settings.quarterTime * 60 * 1000,
            isPlaying: false
          }];
        }
      });

      return {
        ...prev,
        scores: newScores
      };
    });
  };

  return (
    <div className={styles.container}>
      <div className={styles.mainContent}>
        {isWideScreen && (
          <div className={styles.sidebar}>
            <div className={styles.sidePanel}>
              <h2>설정</h2>
              <SettingsModal
                settings={settings}
                onSettingsChange={handleSettingsChange}
                onClose={() => {}}
                isWideScreen={true}
              />
              <div className={styles.testSection}>
                <button 
                  className={styles.testButton}
                  onClick={handleForceKeeperChange}
                >
                  키퍼 교체 테스트
                </button>
              </div>
            </div>
          </div>
        )}

        <div className={styles.mainArea}>
            {/* 쿼터 리스트 */}
            <div className={styles.quarterList}>
            <div className={styles.quarterGrid}>

                {/* 모바일 설정 버튼 */}
                {!isWideScreen && (
                <>
                    <button 
                    className={styles.settingsButton}
                    onClick={() => setShowSettingsModal(true)}
                    >
                    설정
                    </button>
                </>
                )}
                {generateQuarterList().map((quarterData) => (
                <div
                    key={quarterData.quarter}
                    className={`${styles.quarterItem} ${
                    quarterData.quarter === selectedQuarter ? styles.selected : ''
                    } ${
                    quarterData.quarter === matchState.currentQuarter ? styles.current : ''
                    }`}
                    onClick={() => handleQuarterSelect(quarterData.quarter)}
                >
                    <div className={styles.quarterHeader}>
                        <span className={styles.quarterNumber}>
                            {quarterData.quarter}Q
                        </span>
                        <div className={styles.progressBar}>
                            <div 
                                className={`${styles.progress} ${quarterData.isPlaying ? styles.playing : ''}`}
                                style={{
                                    width: quarterData.remainingTime <= 0 
                                      ? '100%' 
                                      : `${100 - (quarterData.remainingTime / (settings.quarterTime * 60 * 1000)) * 100}%`
                                }}
                            />
                        </div>
                    </div>
                    <div className={styles.quarterScores}>
                    {quarterData.teams.map(team => (
                        <div key={team} className={`${styles.quarterTeamScore} ${styles[team]}`}>
                        <span className={styles.teamName}>{team.toUpperCase()}</span>
                        <span className={styles.score}>{quarterData.scores[team] || 0}</span>
                        </div>
                    ))}
                    </div>
                </div>
                ))}
            </div>
            </div>
          <div className={styles.board}>
            <div className={styles.timeContainer}>
                <div className={styles.quarter}>Quarter {selectedQuarter}</div>
                <div className={styles.timeLabel}>남은 시간</div>
                <div className={styles.time}>{getDisplayTime()}</div>
                <div className={styles.timeControls}>
                <div className={styles.timeButtonGroup}>
                    <button
                    onClick={handlePlayButton}
                    className={`${styles.timeButton} ${getPlayButtonStyle()}`}
                    disabled={matchState.remainingTime <= 0}
                    >
                    {getPlayButtonText()}
                    </button>
                    <button
                    onClick={onResetTime}
                    className={styles.timeButton}
                    disabled={matchState.remainingTime === settings.quarterTime * 60 * 1000}
                    >
                    초기화
                    </button>
                </div>
                </div>
            </div>
            <div className={styles.scoreContainer}>
              {getCurrentMatchTeams().map((team, index) => (
                <React.Fragment key={team}>
                  <div className={styles.scoreColumn}>
                    <div className={`${styles.scoreLabel} ${styles[team]}`}>
                      {team.toUpperCase()}
                    </div>
                    <div className={`${styles.scoreValue} ${styles[team]}`}>
                      {matchState.scores[team]}
                    </div>
                    <div className={styles.scoreControls}>
                      <button
                        className={styles.scoreButton}
                        onClick={() => updateScore(team, -1)}
                      >
                        -
                      </button>
                      <button
                        className={styles.scoreButton}
                        onClick={() => updateScore(team, 1)}
                      >
                        +
                      </button>
                    </div>
                    <div className={`${styles.keeperInfo} ${styles[team]}`}>
                      {calculateCurrentKeeper(team) && (
                        <>
                          <div className={styles.keeperLabel}>
                            {calculateCurrentKeeper(team).label}
                          </div>
                          <div className={styles.keeperNumbers}>
                            {calculateCurrentKeeper(team).numbers.map((num, index) => (
                              <span key={index} className={styles.keeperNumber}>{num}</span>
                            ))}
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                  {index === 0 && (
                    <div className={styles.timeSection}>
                      <div className={styles.quarter}>Quarter {selectedQuarter}</div>
                      <div className={styles.timeLabel}>남은 시간</div>
                      <div className={styles.time}>{getDisplayTime()}</div>
                      <div className={styles.timeControls}>
                        <button
                          onClick={handlePlayButton}
                          className={`${styles.timeButton} ${getPlayButtonStyle()}`}
                        >
                          {getPlayButtonText()}
                        </button>
                        <div className={styles.timeButtonGroup}>
                          <button
                            onClick={onResetTime}
                            className={styles.timeButton}
                          >
                            초기화
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </React.Fragment>
              ))}
            </div>
            {shouldShowKeeperChange() && (
              <div className={styles.keeperChangeAlert}>
                공이 나가면 키퍼를 교체해주세요
              </div>
            )}
          </div>
        </div>
      </div>

        {showSettingsModal && (
        <SettingsModal
            settings={settings}
            onSettingsChange={handleSettingsChange}
            onClose={() => setShowSettingsModal(false)}
            isWideScreen={false}
        />
        )}
      
      {/* 키퍼 교체 알림 메시지 */}
      {showKeeperChangeAlert && (
        <div className={styles.keeperChangeAlert}>
          공이 나가면 키퍼를 교체해주세요
        </div>
      )}
    </div>
  );
}

export default MatchBoard; 